import type { OfferIncentives } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const merge = require('lodash.merge');
import { KEYID_DELIMETER } from '../types/commonTypes';

/**
 * Merges incentives from previous and current state based on offer type
 * Combines incentive data structures while maintaining separation by offer type and conditional/nonconditional categories
 *
 * @param prevState - Previous incentives state
 * @param currState - Current incentives state
 * @param currOfferType - The current offer type being processed ('cash' | 'finance' | 'lease')
 * @param skipNullIds - drop `null` ids as well as `undefined` ones
 * @returns Merged OfferIncentives object with combined data from both states
 */
export const mergeIncentives = (
    prevState: OfferIncentives = {} as OfferIncentives,
    currState: OfferIncentives = {} as OfferIncentives,
    currOfferType: 'cash' | 'finance' | 'lease' | 'balloon',
    skipNullIds = false
): OfferIncentives => {
    const offerTypes = [CASH, FINANCE, LEASE] as const;
    const incentiveTypes = { conditionals: 'conditional', nonConditionals: 'nonconditional' } as const;
    const incentiveKeys = ['conditionals', 'nonConditionals'] as const;

    const mergedIncentives: OfferIncentives = {
        byId: {},
        allIds: [],
        byOfferType: {
            finance: {
                conditionals: [],
                nonConditionals: []
            },
            lease: {
                conditionals: [],
                nonConditionals: []
            },
            cash: {
                conditionals: [],
                nonConditionals: []
            }
        },
        mappings: {}
    };

    const prevIncentives = merge({}, prevState);
    const currIncentives = merge({}, currState);

    offerTypes.forEach((offerType) => {
        const sourceIncentives = currOfferType === offerType ? currIncentives : prevIncentives;
        const sourceOfferType = sourceIncentives?.byOfferType || {};
        const sourceIncentive = sourceOfferType[offerType];

        if (sourceIncentive) {
            incentiveKeys.forEach((incentiveKey) => {
                const incentiveIds =
                    (sourceIncentive as any)[incentiveKey]?.filter((id: any) => (skipNullIds ? id != null : id !== undefined)) ??
                    [];

                incentiveIds.forEach((id: any) => {
                    const idParts = [incentiveTypes[incentiveKey], offerType, id];
                    const incentiveId = idParts.join(KEYID_DELIMETER);

                    mergedIncentives.byId[incentiveId] = sourceIncentives.byId[incentiveId] || {};
                    mergedIncentives.allIds.push(incentiveId);
                    mergedIncentives.mappings[incentiveId] = id;
                    (mergedIncentives.byOfferType[offerType] as any)[incentiveKey].push(id);
                });
            });
        }
    });

    // Merge any additional properties from current incentives
    Object.keys(currIncentives).forEach((key) => {
        if (!(mergedIncentives as any)[key]) {
            (mergedIncentives as any)[key] = (currIncentives as any)[key];
        }
    });

    // Merge any additional properties from previous incentives
    Object.keys(prevIncentives).forEach((key) => {
        if (!(mergedIncentives as any)[key]) {
            (mergedIncentives as any)[key] = (prevIncentives as any)[key];
        }
    });

    return mergedIncentives;
};
